// hooks/useDataLayer.ts

import { useCallback } from "react";
import { useSiteRegion } from "./useSiteSection";
import { Item, useGetCartByIdQuery, useGetCartCurrentQuery } from "store/abcoApi";
import { useLocalSearchParams, useSegments } from "expo-router";
import { add } from "date-fns";

type DataLayerItem = {
	item_id: string;
	item_name: string;
	quantity?: number;
	price?: number;
	site_id?: number | undefined;
	is_rechargeable?: boolean;
	category?: string | null;
	brand?: string | null;
	item_category?: string | null;
	item_category2?: string | null;
	item_category3?: string | null;
	item_category4?: string | null;
	item_brand?: string | null;
};

type DataLayerEvent = {
	event: string;
	ecommerce: {
		currency?: string;
		value?: number;
		items: DataLayerItem[];
		transaction_id?: string;
		tax?: number;
		shipping?: number;
	};
};

interface WindowWithDataLayer extends Window {
	dataLayer?: (DataLayerEvent | Record<string, any>)[];
}

export const useDataLayer = () => {
	const region = useSiteRegion();
	const currency = region === "au" ? "AUD" : "NZD";

	const { id } = useLocalSearchParams<{ id?: string }>();
	const segments = useSegments();
	const isCartIdPage = segments[0] === "cart" && segments.length > 1;

	const {
		data: cart,
		isSuccess,
	} = isCartIdPage && id
		? useGetCartByIdQuery({ id: parseInt(id) })
		: useGetCartCurrentQuery();

	const mapToDataLayerItem = useCallback(
		(item: Item, overrideQty?: number): DataLayerItem => ({
			item_id: item.stockcode || '',
			item_name: item.name || '',
			quantity: overrideQty,
			price: item.unitPrice,
			site_id: cart?.siteId ?? undefined,
			is_rechargeable: item.rechargeable ?? false,
			item_category: item.financeCategory || null,
			item_category2: item.category1 || null,
			item_category3: item.category2 || null,
			item_category4: item.category3 || null,
			item_brand: item.brand || null,
		}),
		[cart?.siteId]
		);

		const getItemsFromCart = useCallback((): DataLayerItem[] => {
			return (cart?.lines || []).map((line) =>
				mapToDataLayerItem(line.item, line.quantity)
			);
		}, [cart?.lines, mapToDataLayerItem]);

		const getCartValue = useCallback((): number => {
		return (cart?.lines || []).reduce((sum, line) => {
			const qty = line.quantity ?? 0;
			const price = line.item?.unitPrice ?? 0;
			return sum + qty * price;
		}, 0);
	}, [cart?.lines]);

	const pushToDataLayer = useCallback((eventData: DataLayerEvent) => {

		// Ensure this code only runs in the browser
		if (typeof window === "undefined") return;

		const win = window as WindowWithDataLayer;

		// Ensure dataLayer is always an array (Google's recommended pattern)
		if (!Array.isArray(win.dataLayer)) {
			win.dataLayer = [];
		}

		// Clear previous ecommerce data to prevent stale values
		// @ts-ignore
		win.dataLayer.push({ ecommerce: null });

		// Push the new event data
		win.dataLayer.push(eventData);
	}, []);

	const pushGenericEvent = useCallback((eventData: Record<string, any>) => {
		// Ensure this code only runs in the browser
		if (typeof window === "undefined") return;

		const win = window as WindowWithDataLayer;

		// Ensure dataLayer is always an array (Google's recommended pattern)
		if (!Array.isArray(win.dataLayer)) {
			win.dataLayer = [];
		}

		// Push the generic event data
		win.dataLayer.push(eventData);
	}, []);

	const addToCart = useCallback(
		(item: Item, quantityDelta: number) => {
			if (quantityDelta <= 0) return;

			const mapped = mapToDataLayerItem(item, quantityDelta);
			const value = (mapped.quantity ?? 0) * (mapped.price ?? 0);
			pushToDataLayer({
				event: "add_to_cart",
				ecommerce: {
					currency,
					value,
					items: [mapped],
				},
			});
		},
		[pushToDataLayer, currency, mapToDataLayerItem]
	);

	const removeFromCart = useCallback(
		(item: any, quantityDelta: number) => {
			if (quantityDelta <= 0) return;

			const mapped = mapToDataLayerItem(item, quantityDelta);
			const value = (mapped.quantity ?? 0) * (mapped.price ?? 0);
			pushToDataLayer({
				event: "remove_from_cart",
				ecommerce: {
					currency,
					value,
					items: [mapped],
				},
			});
		},
		[pushToDataLayer, currency, mapToDataLayerItem]
	);

	const addShippingInfo = useCallback(
		() => {
			const items = getItemsFromCart();
			const value = getCartValue();
			pushToDataLayer({
				event: "add_shipping_info",
				ecommerce: {
					currency,
					value,
					items,
				},
			});
		},
		[pushToDataLayer, getItemsFromCart, getCartValue, currency]
	);

	const addPaymentInfo = useCallback(
		() => {
			const items = getItemsFromCart();
			const value = getCartValue();
			pushToDataLayer({
				event: "add_payment_info",
				ecommerce: {
					currency,
					value,
					items,
				},
			});
		},
		[pushToDataLayer, getItemsFromCart, getCartValue, currency]
	);

	const viewCart = useCallback(() => {
		const items = getItemsFromCart();
		const value = getCartValue();
		pushToDataLayer({
			event: "view_cart",
			ecommerce: {
				currency,
				value,
				items,
			},
		});
	}, [pushToDataLayer, getItemsFromCart, getCartValue, currency]);

	const viewItem = useCallback(
		(item: Item, value = 0) => {
			const mapped = mapToDataLayerItem(item);
			pushToDataLayer({
				event: "view_item",
				ecommerce: {
					currency,
					value,
					items: [mapped],
				},
			});
		},
		[pushToDataLayer, currency, mapToDataLayerItem]
	);

	const purchase = useCallback(() => {
		const items = getItemsFromCart();
		const value = getCartValue();
		pushToDataLayer({
			event: "purchase",
			ecommerce: {
				transaction_id: cart?.id?.toString() || "",
				currency,
				value,
				items,
				tax: cart?.taxAmount || 0,
				shipping: cart?.freightCharge || 0,
			},
		});
	}, [pushToDataLayer, getItemsFromCart, getCartValue, currency]);

	return {
		pushToDataLayer,
		pushGenericEvent,
		removeFromCart,
		addToCart,    
		viewItem,
		purchase,
		viewCart,
		getItemsFromCart,
		addShippingInfo,
		addPaymentInfo,
	};
};
